/**
 * ProgramDetailsPage Component
 * Sports Season Style - ESPN/Sky Sports Inspired
 * Route: /clubs/:clubId/programs/:programId
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FaArrowLeft,
  FaUsers, 
  FaMoneyBillWave, 
  FaCalendarAlt, 
  FaMapMarkerAlt,
  FaGamepad,
  FaVenusMars,
  FaCog,
  FaShare,
  FaCheck,
  FaEdit,
  FaClock,
  FaUserFriends,
  FaChevronRight,
  FaPlay,
  FaHourglassHalf,
  FaExclamationCircle,
  FaCheckCircle
} from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { getProgramMembers } from '../../api/programs';
import { useUser } from '../../context/UserContext';
import { useFollowingStatus } from '../../hooks/useFollowingStatus';
import MembershipStatusBadge from '../../components/Program/MembershipStatusBadge';
import FollowButton from '../../components/Program/FollowButton';
import SmartActionButton from './components/SmartActionButton';
import JoinClubModal from '../../components/Club/JoinClubModal';
import ProgramWizard from '../../components/ProgramWizard/ProgramWizard';
import { getSportIcon, formatProgramPrice, getRegistrationDates } from './utils/programHelpers';

// Tab content components
import OverviewTab from './components/ProgramDetailPanel/tabs/OverviewTab';
import ScheduleTab from './components/ProgramDetailPanel/tabs/ScheduleTab';
import FeesTab from './components/ProgramDetailPanel/tabs/FeesTab';
import LocationsTab from './components/ProgramDetailPanel/tabs/LocationsTab';
import StaffTab from './components/ProgramDetailPanel/tabs/StaffTab';
import ResourcesTab from './components/ProgramDetailPanel/tabs/ResourcesTab';
import ReviewsTab from './components/ProgramDetailPanel/tabs/ReviewsTab';

import styles from './ProgramDetailsPage.module.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'fees', label: 'Fees' },
  { id: 'locations', label: 'Locations' },
  { id: 'staff', label: 'Staff' },
  { id: 'resources', label: 'Resources' },
  { id: 'reviews', label: 'Reviews' }
];

const ProgramDetailsPage = () => {
  const { clubId, programId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile, userClubs, refreshUserClubs } = useUser();
  const followingStatus = useFollowingStatus(programId);
  
  // Data state
  const [program, setProgram] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bannerError, setBannerError] = useState(false);
  
  // Membership state
  const [myMembershipStatus, setMyMembershipStatus] = useState(null);
  const [myCanApply, setMyCanApply] = useState(false);
  const [enrolledCount, setEnrolledCount] = useState(0);
  const [membershipLoading, setMembershipLoading] = useState(true);
  
  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [showJoinClubModal, setShowJoinClubModal] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Check user permissions
  const canManage = userClubs?.some(m => {
    const membershipClubId = m.club?._id || m.club;
    const role = (m.role || '').toLowerCase();
    return String(membershipClubId) === String(clubId) && 
           ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin', 'programdirector'].includes(role);
  });

  const isClubManager = userClubs?.some(m => {
    const membershipClubId = m.club?._id || m.club;
    const role = (m.role || '').toLowerCase();
    return String(membershipClubId) === String(clubId) && 
           ['creator', 'owner', 'manager', 'admin', 'programdirector'].includes(role);
  });

  // Fetch program and club data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const programRes = await fetch(`${backendUrl}/api/programs/${programId}`);
        if (!programRes.ok) throw new Error('Program not found');
        const programData = await programRes.json();
        setProgram(programData);

        const actualClubId = programData.clubId?._id || programData.clubId || clubId;
        if (actualClubId) {
          const clubRes = await fetch(`${backendUrl}/api/clubs/${actualClubId}`);
          if (clubRes.ok) {
            const clubData = await clubRes.json();
            setClub(clubData);
          }
        }
      } catch (err) {
        console.error('Error fetching program:', err);
        setError(err.message || 'Failed to load program');
      } finally {
        setLoading(false);
      }
    };

    if (programId) {
      fetchData();
    }
  }, [programId, clubId]);

  // Fetch membership status
  useEffect(() => {
    const fetchMembershipData = async () => {
      if (!programId || !userProfile?._id) {
        setMembershipLoading(false);
        return;
      }

      setMembershipLoading(true);

      try {
        const enrolled = await getProgramMembers(programId, { status: 'Enrolled' });
        setEnrolledCount(Array.isArray(enrolled) ? enrolled.length : 0);
      } catch (err) {
        setEnrolledCount(0);
      }

      try {
        const all = await getProgramMembers(programId);
        const mine = Array.isArray(all) 
          ? all.find(m => (m.userId || m.user?._id) === userProfile._id)
          : null;
        setMyMembershipStatus(mine?.status || null);
        setMyCanApply(mine?.canApplyDespiteRestrictions || false);
      } catch (err) {
        setMyMembershipStatus(null);
        setMyCanApply(false);
      } finally {
        setMembershipLoading(false);
      }
    };

    fetchMembershipData();
  }, [programId, userProfile?._id]);

  // Refresh membership state
  const refreshMembershipState = useCallback(async () => {
    if (!programId) return;

    try {
      const enrolled = await getProgramMembers(programId, { status: 'Enrolled' });
      setEnrolledCount(Array.isArray(enrolled) ? enrolled.length : 0);

      const all = await getProgramMembers(programId);
      const mine = Array.isArray(all) 
        ? all.find(m => (m.userId || m.user?._id) === userProfile?._id)
        : null;
      setMyMembershipStatus(mine?.status || null);
      setMyCanApply(mine?.canApplyDespiteRestrictions || false);
    } catch (err) {
      console.error('Error refreshing membership state:', err);
    }
  }, [programId, userProfile?._id]);

  // Handle share
  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: program?.name || 'Program Details',
          text: `Check out ${program?.name} at ${club?.name || 'our club'}!`,
          url: url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleJoinClubRequired = () => setShowJoinClubModal(true);
  const handleJoinSuccess = () => {
    setShowJoinClubModal(false);
    refreshUserClubs();
    refreshMembershipState();
  };

  const handleEditProgram = () => setShowEditModal(true);
  const handleCloseEditModal = () => setShowEditModal(false);

  const handleEditSubmit = async () => {
    setShowEditModal(false);
    try {
      const programRes = await fetch(`${backendUrl}/api/programs/${programId}`);
      if (programRes.ok) {
        const programData = await programRes.json();
        setProgram(programData);
      }
    } catch (err) {
      console.error('Error refreshing program:', err);
    }
  };

  // Render tab content
  const renderTabContent = () => {
    if (!program) return null;

    switch (activeTab) {
      case 'overview':
        return <OverviewTab program={program} clubId={clubId} />;
      case 'schedule':
        return <ScheduleTab program={program} />;
      case 'fees':
        return <FeesTab program={program} />;
      case 'locations':
        return <LocationsTab program={program} />;
      case 'staff':
        return <StaffTab program={program} />;
      case 'resources':
        return <ResourcesTab program={program} />;
      case 'reviews':
        return <ReviewsTab program={program} />;
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <ClipLoader color="#A4EB3F" loading={true} size={50} />
        <p>Loading program...</p>
      </div>
    );
  }

  // Error state
  if (error || !program) {
    return (
      <div className={styles.errorContainer}>
        <h2>Program Not Found</h2>
        <p>{error || 'The program you are looking for does not exist.'}</p>
        <button 
          className={styles.backButton}
          onClick={() => navigate(clubId ? `/clubs/${clubId}` : '/')}
        >
          <FaArrowLeft /> Back to Club
        </button>
      </div>
    );
  }

  const SportIcon = getSportIcon(program.sport);
  const price = formatProgramPrice(program);
  const actualClubId = program.clubId?._id || program.clubId || clubId;
  const regDates = getRegistrationDates(program);
  const clubColor = club?.primaryColor || '#A4EB3F';
  
  // Calculate capacity info
  const capacity = program.capacity?.max || 0;
  const spotsLeft = Math.max(0, capacity - enrolledCount);
  const isFull = capacity > 0 && enrolledCount >= capacity;
  const hasWaitlist = program.capacity?.waitlistEnabled;
  
  // Get program image URL
  const getProgramImageUrl = () => {
    if (!program.programImage) return null;
    if (typeof program.programImage === 'string') return program.programImage;
    return program.programImage?.transformedUrls?.full || 
           program.programImage?.url || 
           program.programImage;
  };

  // Format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Calculate duration
  const getDuration = () => {
    if (!program.schedule?.startDate || !program.schedule?.endDate) return null;
    const start = new Date(program.schedule.startDate);
    const end = new Date(program.schedule.endDate);
    const weeks = Math.ceil((end - start) / (7 * 24 * 60 * 60 * 1000));
    return `${weeks} week${weeks !== 1 ? 's' : ''}`;
  };

  return (
    <div className={styles.page} style={{ '--club-color': clubColor }}>
      
      {/* ═══════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════ */}
      <section className={styles.hero}>
        {/* Background Image */}
        {getProgramImageUrl() && !bannerError ? (
          <div className={styles.heroBg}>
            <img 
              src={getProgramImageUrl()} 
              alt={`${program.name} banner`}
              className={styles.heroBgImg}
              onError={() => setBannerError(true)}
              loading="eager"
            />
            <div className={styles.heroBgOverlay} />
          </div>
        ) : (
          <div className={styles.heroBgFallback} aria-hidden="true" />
        )}

        {/* Top Navigation */}
        <nav className={styles.topNav}>
          <Link to={`/clubs/${actualClubId}`} className={styles.backBtn}>
            <FaArrowLeft /> <span>Back to {club?.name || 'Club'}</span>
          </Link>
          
          <div className={styles.navRight}>
            <button 
              className={`${styles.navBtn} ${shareSuccess ? styles.success : ''}`}
              onClick={handleShare}
              aria-label={shareSuccess ? 'Link copied to clipboard' : 'Share this program'}
            >
              {shareSuccess ? <FaCheck /> : <FaShare />}
              <span>{shareSuccess ? 'Copied!' : 'Share'}</span>
            </button>
            
            {canManage && (
              <>
                <button 
                  className={styles.navBtn} 
                  onClick={handleEditProgram}
                  aria-label="Edit program details"
                >
                  <FaEdit /> <span>Edit</span>
                </button>
                <button 
                  className={styles.navBtnAccent}
                  onClick={() => navigate(`/clubs/${actualClubId}/programs/${programId}/manage`)}
                  aria-label="Manage program"
                >
                  <FaCog /> <span>Manage</span>
                </button>
              </>
            )}
          </div>
        </nav>

        {/* Hero Content */}
        <div className={styles.heroContent}>
          {/* Left: Program Info */}
          <div className={styles.heroMain}>
            {/* Top Row: Club + Sport badges */}
            <div className={styles.badgeRow}>
              {club && (
                <Link 
                  to={`/clubs/${actualClubId}`} 
                  className={styles.clubBadge}
                  aria-label={`View ${club.name || 'club'} page`}
                >
                  {club.clubLogo && (
                    <img 
                      src={club.clubLogo} 
                      alt={`${club.name || 'Club'} logo`} 
                      className={styles.clubBadgeLogo}
                      loading="lazy" 
                    />
                  )}
                  <span>{club.name || 'Club'}</span>
                </Link>
              )}
              <div className={styles.sportBadge} aria-label={`Sport: ${program.sport || 'Sports'}`}>
                <SportIcon aria-hidden="true" /> {program.sport || 'Sports'}
              </div>
            </div>

            {/* Title + Status Row */}
            <div className={styles.titleRow}>
              <h1 className={styles.programTitle}>{program.name}</h1>
              {!membershipLoading && myMembershipStatus && !(myMembershipStatus === 'Waitlisted' && myCanApply) && (
                <div className={styles.userStatus}>
                  <MembershipStatusBadge status={myMembershipStatus} />
                </div>
              )}
            </div>

            {/* Quick Info Tags */}
            <div className={styles.quickTags}>
              {program.gameFormat && (
                <span className={styles.tag}>
                  <FaGamepad /> {program.gameFormat}
                </span>
              )}
              <span className={styles.tag}>
                <FaUsers /> Ages {program.age?.min || '?'}-{program.age?.max || '?'}
              </span>
              <span className={styles.tag}>
                <FaVenusMars /> {program.gender || 'All'}
              </span>
              {program.locations?.length > 0 && (
                <span className={styles.tag}>
                  <FaMapMarkerAlt /> {program.locations.length} location{program.locations.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Right: CTA Card */}
          <div className={styles.heroCTA}>
            <div className={styles.ctaCard}>
              <div className={styles.priceTag}>
                <span className={styles.priceAmount}>{price}</span>
                {program.fees?.paymentType && (
                  <span className={styles.priceNote}>
                    {program.fees.paymentType === 'one-time' ? 'one-time' : 'per season'}
                  </span>
                )}
              </div>
              
              <div className={styles.ctaButtons}>
                <SmartActionButton
                  program={program}
                  clubId={actualClubId}
                  myMembershipStatus={myMembershipStatus}
                  isClubManager={isClubManager}
                  enrolledCount={enrolledCount}
                  onJoinClubRequired={handleJoinClubRequired}
                  onMembershipStatusChange={setMyMembershipStatus}
                  refreshMembershipState={refreshMembershipState}
                  size="large"
                  showDetailsButton={false}
                  showNotifications={true}
                />
                
                <FollowButton
                  programId={program._id}
                  programName={program.name}
                  className={styles.heroFollowBtn}
                  showText={true}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          STATS TICKER
      ═══════════════════════════════════════════════════ */}
      <section className={styles.statsTicker}>
        <div className={styles.tickerInner}>
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>
              {enrolledCount}{capacity > 0 ? `/${capacity}` : ''}
            </span>
            <span className={styles.statLabel}>Enrolled</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>
              {program.schedule?.frequency?.length || 0}
            </span>
            <span className={styles.statLabel}>Sessions/Week</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>{getDuration() || 'TBD'}</span>
            <span className={styles.statLabel}>Duration</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>
              {formatDate(program.schedule?.startDate).split(',')[0]}
            </span>
            <span className={styles.statLabel}>Starts</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          INFO CARDS ROW
      ═══════════════════════════════════════════════════ */}
      <section className={styles.infoCards}>
        <div className={styles.infoCardsInner}>
          
          {/* Capacity Card */}
          <div className={styles.infoCard}>
            <div className={styles.infoCardHeader}>
              <FaUserFriends className={styles.infoCardIcon} />
              <h3>Roster</h3>
            </div>
            <div className={styles.capacityDisplay}>
              <div className={styles.capacityBar}>
                <div 
                  className={styles.capacityFill} 
                  style={{ 
                    width: `${Math.min(100, capacity > 0 ? (enrolledCount / capacity) * 100 : 0)}%`,
                    background: isFull ? 'var(--error)' : 'var(--club-color, #A4EB3F)'
                  }}
                />
              </div>
              <div className={styles.capacityText}>
                {isFull ? (
                  <span className={styles.capacityFull}>
                    <FaExclamationCircle /> Program Full
                    {hasWaitlist && <span className={styles.waitlistNote}> • Waitlist Open</span>}
                  </span>
                ) : capacity > 0 ? (
                  <span className={styles.spotsLeft}>
                    <FaCheckCircle /> {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} remaining
                  </span>
                ) : (
                  <span className={styles.openEnrollment}>Open Enrollment</span>
                )}
              </div>
            </div>
          </div>

          {/* Schedule Card */}
          <div className={styles.infoCard}>
            <div className={styles.infoCardHeader}>
              <FaCalendarAlt className={styles.infoCardIcon} />
              <h3>Season Dates</h3>
            </div>
            <div className={styles.dateDisplay}>
              <div className={styles.dateRow}>
                <span className={styles.dateLabel}>Start</span>
                <span className={styles.dateValue}>{formatDate(program.schedule?.startDate)}</span>
              </div>
              <div className={styles.dateRow}>
                <span className={styles.dateLabel}>End</span>
                <span className={styles.dateValue}>{formatDate(program.schedule?.endDate)}</span>
              </div>
            </div>
          </div>

          {/* Registration Card */}
          <div className={styles.infoCard}>
            <div className={styles.infoCardHeader}>
              <FaClock className={styles.infoCardIcon} />
              <h3>Registration</h3>
            </div>
            {regDates.hasRegistrationDates ? (
              <div className={styles.regDisplay}>
                <div className={`${styles.regStatus} ${styles[regDates.statusType]}`}>
                  {regDates.statusType === 'open' && <FaCheckCircle />}
                  {regDates.statusType === 'upcoming' && <FaHourglassHalf />}
                  {regDates.statusType === 'closed' && <FaExclamationCircle />}
                  <span>{regDates.statusMessage}</span>
                </div>
                <div className={styles.regDates}>
                  {regDates.minStartDate && (
                    <span>Opens: {regDates.minStartDate.toLocaleDateString()}</span>
                  )}
                  {regDates.maxEndDate && (
                    <span>Closes: {regDates.maxEndDate.toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.regOpen}>
                <FaCheckCircle /> Open Registration
              </div>
            )}
          </div>

        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          MAIN CONTENT
      ═══════════════════════════════════════════════════ */}
      <main className={styles.mainContent}>
        {/* Tab Navigation */}
        <nav className={styles.tabNav} role="tablist" aria-label="Program details sections">
          {TABS.map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              className={`${styles.tabBtn} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div 
          className={styles.tabContent}
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {renderTabContent()}
        </div>
      </main>

      {/* Modals */}
      {showJoinClubModal && club && (
        <JoinClubModal
          club={club}
          onClose={() => setShowJoinClubModal(false)}
          onJoinSuccess={handleJoinSuccess}
          user={user}
        />
      )}

      {showEditModal && program && (
        <ProgramWizard
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          clubId={actualClubId}
          initialData={program}
          onSubmit={handleEditSubmit}
        />
      )}
    </div>
  );
};

export default ProgramDetailsPage;
